import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRightStartOnRectangleIcon,
  ChevronRightIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { useLogin } from "../../hooks/useLogin";
import { useLanguageSpace } from "../../hooks/useLanguageSpace";
import {
  ENABLED_LANGUAGE_SPACE_IDS,
  getLevelOptionsForLanguage,
  getLevelTranslationKey,
  isLevelAllowedForLanguage,
  TEXT_LANGUAGES,
} from "../../constants";
import { useNavigate } from "@tanstack/react-router";
import useTranslation from "../../hooks/useTranslation";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import {
  deriveEffectiveLanguageSpaceScope,
  getAddedLanguageSpacesStorageKey,
} from "../../hooks/useAddedLanguageSpaces";
import { useLoginStatus } from "../../hooks/useLoginStatus";
import { useUserSettings } from "../../hooks/useUserSettings";
import { useUserSettingsUpdate } from "../../hooks/useUserSettingsUpdate";

type SettingsRowProps = {
  label: string;
  to?: string;
  disabled?: boolean;
  onClick?: () => void;
};

export const Route = createFileRoute("/app/space_settings")({
  component: SpaceSettingsPage,
});

function SpaceSettingsPage() {
  const loginStatus = useLoginStatus();
  const userSettings = useUserSettings({
    enabled: loginStatus.data?.state === "logged in",
  });
  const updateUserSettings = useUserSettingsUpdate();
  const { languageSpace, setLanguageSpace } = useLanguageSpace("ENGLISH");
  const navigate = useNavigate();
  const { translate } = useTranslation();
  const addedLanguageSpacesStorageKey = getAddedLanguageSpacesStorageKey(
    loginStatus.data?.state === "logged in" ? loginStatus.data.userId : null,
  );
  const [addedLanguageSpaces] = useLocalStorage<string[]>(
    addedLanguageSpacesStorageKey,
    [],
  );
  const serverAddedLanguageSpaces = Array.isArray(
    userSettings.data?.added_language_spaces,
  )
    ? userSettings.data.added_language_spaces
    : [];

  const resolvedLanguage = languageSpace === "ALL" ? "ENGLISH" : languageSpace;
  const nativeLanguageLabelById: Record<string, string> = {
    ENGLISH: "English",
    ESPERANTO: "Esperanto",
    FRENCH: "Français",
    GERMAN: "Deutsch",
    GREEK: "Ελληνικά",
    GREEK_LATIN_TRANSCRIPTION: "Ελληνικά (Latin)",
    ITALIAN: "Italiano",
    PORTUGUESE_FROM_BRAZIL: "Português (Brasil)",
    PORTUGUESE_FROM_PORTUGAL: "Português (Portugal)",
    RUSSIAN: "Русский",
    RUSSIAN_LATIN_TRANSCRIPTION: "Русский (Latin)",
    SPANISH: "Español",
  };
  const hasPersistedLocalAddedLanguageSpaces =
    typeof window !== "undefined" &&
    window.localStorage.getItem(addedLanguageSpacesStorageKey) !== null;
  const hasPersistedAddedLanguageSpaces =
    serverAddedLanguageSpaces.length > 0 || hasPersistedLocalAddedLanguageSpaces;
  const { effectiveLanguageSpaceSet, hasAddedAllEnabledSpaces } =
    deriveEffectiveLanguageSpaceScope({
      addedLanguageSpaces:
        serverAddedLanguageSpaces.length > 0
          ? serverAddedLanguageSpaces
          : addedLanguageSpaces,
      enabledLanguageSpaceIds: ENABLED_LANGUAGE_SPACE_IDS,
      hasPersistedAddedLanguageSpaces,
      fallbackLanguageSpaceId: resolvedLanguage,
    });
  const visibleLanguageSpaces = TEXT_LANGUAGES.filter((language) =>
    effectiveLanguageSpaceSet.has(language.id),
  ).sort((a, b) => {
    if (a.id === resolvedLanguage) return -1;
    if (b.id === resolvedLanguage) return 1;
    const aLabel = nativeLanguageLabelById[a.id] ?? a.pretty;
    const bLabel = nativeLanguageLabelById[b.id] ?? b.pretty;
    return aLabel.localeCompare(bLabel, undefined, { sensitivity: "base" });
  });
  const languageLevels = userSettings.data?.language_levels ?? {};

  return (
    <div className="mx-auto w-full max-w-3xl space-y-5 px-4 py-5 md:px-6 md:py-6">
      <section>
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
          SPACES
        </p>
        <div className="space-y-3">
          {visibleLanguageSpaces.map((space) => {
            const isCurrent = space.id === resolvedLanguage;
            const savedLevel = isLevelAllowedForLanguage(languageLevels[space.id], space.id)
              ? languageLevels[space.id]
              : "A2";
            return (
              <div
                key={space.id}
                className={`rounded-2xl border px-4 py-3 ${
                  isCurrent
                    ? "border-slate-300 bg-slate-50"
                    : "border-slate-200 bg-white"
                }`}
              >
                <button
                  type="button"
                  onClick={() => {
                    if (isCurrent) {
                      return;
                    }
                    setLanguageSpace(space.id);
                    void navigate({ to: "/app/space_settings" });
                  }}
                  className={`flex w-full items-center gap-3 text-left transition ${
                    isCurrent ? "" : "hover:bg-slate-50"
                  }`}
                  aria-current={isCurrent ? "page" : undefined}
                >
                  <span
                    className="h-11 w-11 rounded-2xl border border-slate-200"
                    style={{ backgroundColor: space.accentBg }}
                    aria-hidden="true"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-lg font-semibold text-slate-900">
                      {nativeLanguageLabelById[space.id] ?? space.pretty}
                    </span>
                  </span>
                  {isCurrent ? (
                    <span className="rounded-full border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700">
                      {translate("space_settings_active_badge")}
                    </span>
                  ) : (
                    <ChevronRightIcon
                      className="h-5 w-5 shrink-0 text-slate-400"
                      aria-hidden="true"
                    />
                  )}
                </button>
                <div className="mt-3 border-t border-slate-200 pt-3">
                  <label
                    htmlFor={`language-level-${space.id}`}
                    className="block text-sm font-semibold text-slate-900"
                  >
                    {translate("space_settings_language_level_label")}
                  </label>
                  <p className="mt-1 text-xs text-slate-600">
                    {translate("space_settings_language_level_hint")}
                  </p>
                  <select
                    id={`language-level-${space.id}`}
                    data-testid={`space-level-select-${space.id}`}
                    value={savedLevel}
                    onChange={(event) => {
                      updateUserSettings.mutate({
                        userSettingsDto: {
                          language_levels: {
                            ...languageLevels,
                            [space.id]: event.target.value,
                          },
                        },
                      });
                    }}
                    className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-base text-slate-800"
                  >
                    {getLevelOptionsForLanguage(space.id).map((level) => (
                      <option key={level} value={level}>
                        {`${level} · ${translate(getLevelTranslationKey(level))}`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            );
          })}
          {!hasAddedAllEnabledSpaces ? (
            <button
              type="button"
              data-testid="space-settings-add-language-space"
              onClick={() =>
                void navigate({
                  to: "/app/start-learning",
                })
              }
              className="flex w-full items-center gap-3 rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-3 text-left transition hover:bg-slate-50"
            >
              <span className="h-11 w-11 rounded-2xl border border-slate-200 bg-slate-50" />
              <span className="min-w-0 flex-1">
                <span className="block text-lg font-semibold text-slate-900">
                  {translate("space_settings_add_new_space")}
                </span>
              </span>
              <PlusIcon className="h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" />
            </button>
          ) : null}
        </div>
      </section>

      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          SYSTEM
        </p>
        <div className="overflow-hidden rounded-2xl border border-slate-200">
          {[
            {
              label: "Account",
              to: "/app/account?from=space_settings",
            },
            {
              label: "Interface language",
              to: "/app/settings?from=space_settings",
            },
            {
              label: "Translation language",
              to: "/app/settings?from=space_settings",
            },
            {
              label: "Weekly summary",
              to: "/app/settings?from=space_settings",
            },
          ]
            .filter((row) => row.label.trim().length > 0)
            .map((row) => (
            <SettingsRow key={row.label} {...row} />
            ))}
        </div>
      </div>

      <div>
        <LogoutRow />
      </div>
    </div>
  );
}

function SettingsRow({ label, to, disabled, onClick }: SettingsRowProps) {
  const baseClassName =
    "flex w-full items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 text-left last:border-b-0";

  if (disabled) {
    return (
      <div className={`${baseClassName} cursor-not-allowed opacity-55`} aria-disabled="true">
        <p className="text-sm font-semibold text-slate-700">{label}</p>
      </div>
    );
  }

  if (to) {
    return (
      <Link to={to} className={`${baseClassName} transition hover:bg-slate-50`}>
        <p className="text-sm font-semibold text-slate-900">{label}</p>
        <ChevronRightIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${baseClassName} transition hover:bg-slate-50`}
    >
      <p className="text-sm font-semibold text-slate-900">{label}</p>
      <ChevronRightIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
    </button>
  );
}

function LogoutRow() {
  const login = useLogin();

  return (
    <button
      type="button"
      onClick={() => login.logoutMutation.mutate()}
      className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-left transition hover:bg-slate-50"
    >
      <div>
        <p className="text-sm font-semibold text-slate-900">Sign out</p>
      </div>
      <ArrowRightStartOnRectangleIcon
        className="h-5 w-5 shrink-0 text-slate-500"
        aria-hidden="true"
      />
    </button>
  );
}
